/**
 * Who gets asked to set a password, and when they stop being asked.
 *
 * Karr signed people in with a magic link every single time, which is fine once
 * and tiresome by the fourth week — and it made every sign-in depend on an email
 * arriving, being unread, and not having been scanned (see `linkError.ts` for
 * what happens when that last one fails).
 *
 * So: **sign-up stays link-or-Google only**, and a password is offered once the
 * mailbox has already been proven. That ordering is the point. A password is not
 * a spam filter — anyone can invent one — whereas clicking a link that only
 * arrives in a real inbox is a genuine proof of control. Asking for the password
 * afterwards keeps the proof and drops the tedium.
 *
 * A pure predicate on purpose: "should this person see the set-password screen"
 * is the kind of branch that quietly rots inside a route component.
 *
 * @since 2026-08-01
 */

/** Just enough of a Supabase `User` to decide. */
export interface PasswordPromptUser {
    app_metadata?: { provider?: string | null } | null;
    user_metadata?: {
        /** Set by SetPassword once a password actually saved. */
        has_password?: boolean | null;
        /** Set when they chose "Skip for now" — we ask once, not every visit. */
        password_prompt_dismissed?: boolean | null;
    } | null;
}

/** Keys we write into `user_metadata`; named here so route code can't typo one. */
export const PASSWORD_META = {
    has: 'has_password',
    dismissed: 'password_prompt_dismissed',
} as const;

/**
 * Should we interrupt this person with the set-password screen?
 *
 * False for a Google account: they already have a one-click way in, so a Karr
 * password would be a second credential to forget for no gain.
 *
 * False once they have one, and false once they have said no — being asked
 * again every time you open the app is its own kind of broken.
 *
 * @param user - the signed-in Supabase user (null while loading)
 * @returns whether to route to /auth/set-password
 */
export function shouldOfferPassword(user: PasswordPromptUser | null | undefined): boolean {
    if (!user) return false;
    // `provider` is 'email' for both magic-link and password accounts, 'google'
    // for OAuth. Absent on a shape we don't recognise — don't interrupt then.
    const provider = user.app_metadata?.provider;
    if (provider !== 'email') return false;
    const meta = user.user_metadata ?? {};
    if (meta.has_password) return false;
    if (meta.password_prompt_dismissed) return false;
    return true;
}

/**
 * Does this account already have a password? Drives the account menu wording
 * ("Set a password" vs "Change password") and the sign-in screen's advice.
 */
export function hasPassword(user: PasswordPromptUser | null | undefined): boolean {
    return Boolean(user?.user_metadata?.has_password);
}
